import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import LogoCarousel from '@/components/LogoCarousel';

import resumeMatchAnalysisImage from '@/assets/resume-match-analysis-final.png';


import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

import { AppLayout } from "@/components/AppLayout";
import { ClientDashboardHome } from "@/components/dashboard/ClientDashboardHome";
import { useTailoringRoute } from "@/context/TailoringModeContext";
import { ProfileDialog } from "@/components/ProfileDialog";
import { JobWizard } from "@/components/JobWizard/JobWizard";
import { JobLibrary } from "@/components/JobLibrary";
import { ResumeAnalysis } from "@/components/ResumeAnalysis";
import { ResumeLibrary } from "@/components/ResumeLibrary";
import VectorShiftTest from "./VectorShiftTest";
import { JobLibraryService, SavedJobDescription } from "@/services/jobLibraryService";
import linkedinLogo from '@/assets/logos/linkedin-logo.png';
import { User, Settings, LogOut, ChevronDown, ChevronRight, FileText, Briefcase, Sparkles, ArrowRight, Target, Zap, CheckCircle, Star, TrendingUp, BarChart3, Users, Upload, Home, FileEdit, Shield, Library, Linkedin, Edit3 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { LockedToolOverlay } from "@/components/LockedToolOverlay";
import { TailorWizMarketingHome } from "@/components/TailorWizMarketingHome";
type AppView = 'home' | 'job-wizard' | 'job-library' | 'resume-analysis' | 'resume-library' | 'resume-match-tests' | 'vectorshift-test' | 'vectorshift-direct-test' | 'libraries';
export default function Index() {
  const {
    user,
    profile,
    signOut,
    loading: authLoading
  } = useAuth();
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { route: tailoringRoute } = useTailoringRoute();
  const [currentView, setCurrentView] = useState<AppView>('home');
  const [savedJobs, setSavedJobs] = useState<SavedJobDescription[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [resumeLibraryInitialTab, setResumeLibraryInitialTab] = useState<'analyzed' | 'saved'>('analyzed');
  const [jobWizardCallback, setJobWizardCallback] = useState<((job: SavedJobDescription) => void) | null>(null);
  const [customBadgeImage, setCustomBadgeImage] = useState<string>("/lovable-uploads/81df72d4-c791-4317-96cd-e755b8c0cdec.png");
  // showEditorDialog state removed - navigating directly to tailoring

  // Handle URL parameters for navigation back from saved resume detail
  useEffect(() => {
    const view = searchParams.get('view') as AppView;
    const tab = searchParams.get('tab') as 'analyzed' | 'saved';
    if (view && ['job-wizard', 'job-library', 'resume-analysis', 'resume-library', 'resume-match-tests', 'vectorshift-test'].includes(view)) {
      setCurrentView(view);
      if (view === 'resume-library' && tab === 'saved') {
        setResumeLibraryInitialTab('saved');
      }
    } else {
      setCurrentView('home');
    }
  }, [searchParams]);
  useEffect(() => {
    if (user) {
      loadSavedJobs();
    }
  }, [user]);
  const loadSavedJobs = async () => {
    try {
      setLoadingJobs(true);
      const jobs = await JobLibraryService.getSavedJobs();
      setSavedJobs(jobs);
    } catch (error) {
      console.error('Failed to load saved jobs:', error);
      toast({
        title: "Error",
        description: "Failed to load your saved jobs",
        variant: "destructive"
      });
    } finally {
      setLoadingJobs(false);
    }
  };
  const handleJobSaved = (newJob: SavedJobDescription) => {
    setSavedJobs(prev => [newJob, ...prev]);

    // If there's a callback waiting (from resume analysis), call it
    if (jobWizardCallback) {
      jobWizardCallback(newJob);
      setJobWizardCallback(null); // Clear the callback
      setCurrentView('resume-analysis'); // Go back to resume analysis
    } else {
      setCurrentView('job-library'); // Normal flow
    }
  };
  const handleJobDeleted = async () => {
    await loadSavedJobs(); // Refresh the list
  };
  const handleOpenJobWizard = () => {
    setCurrentView('job-wizard');
  };
  const handleOpenJobLibrary = () => {
    setCurrentView('job-library');
  };
  const handleBackToHome = () => {
    setCurrentView('home');
  };
  const handleOpenResumeAnalysis = () => {
    setCurrentView('resume-analysis');
  };
  const handleTailorResume = () => {
    navigate(tailoringRoute);
  };
  const handleOpenVectorShiftTest = () => {
    setCurrentView('vectorshift-test');
  };
  const handleOpenVectorShiftDirectTest = () => {
    setCurrentView('vectorshift-direct-test');
  };
  const handleOpenResumeLibrary = () => {
    setCurrentView('resume-library');
  };
  const handleOpenResumeMatchTests = () => {
    setCurrentView('resume-match-tests');
  };
  const handleOpenLibraries = () => {
    setCurrentView('libraries');
  };
  const handleProfileOpen = () => {}; // No longer needed but kept for AppSidebar interface

  const handleUploadNewResume = () => {
    // Create a file input to upload resume to saved resumes
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx';
    input.onchange = async e => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file && user?.id) {
        try {
          // Extract text from file
          const {
            FileTextExtractor
          } = await import('@/utils/fileTextExtractor');
          const extractionResult = await FileTextExtractor.extractText(file);
          if (!extractionResult.success) {
            toast({
              title: "Error",
              description: "Failed to extract text from resume file",
              variant: "destructive"
            });
            return;
          }

          // Save to ResumeLibraryService (saved resumes)
          const {
            ResumeLibraryService
          } = await import('@/services/resumeLibraryService');
          await ResumeLibraryService.saveResume(file.name.replace(/\.[^/.]+$/, ""),
          // title - Remove file extension
          extractionResult.text!,
          // content
          file.name,
          // fileName
          file.type // fileType
          );
          toast({
            title: "Resume Uploaded",
            description: "Your resume has been saved to your library"
          });

          // Navigate to resume library and show saved resumes tab
          setResumeLibraryInitialTab('saved');
          setCurrentView('resume-library');
        } catch (error) {
          console.error('Error uploading resume:', error);
          toast({
            title: "Upload Failed",
            description: "Failed to upload resume. Please try again.",
            variant: "destructive"
          });
        }
      }
    };
    input.click();
  };
  const handleEditBadgeImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async e => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          // Create object URL for immediate preview
          const objectUrl = URL.createObjectURL(file);
          setCustomBadgeImage(objectUrl);
          toast({
            title: "Image Updated",
            description: "Your custom badge image has been applied"
          });
        } catch (error) {
          console.error('Error uploading image:', error);
          toast({
            title: "Upload Failed",
            description: "Failed to upload image. Please try again.",
            variant: "destructive"
          });
        }
      }
    };
    input.click();
  };
  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Signed Out",
        description: "You have been signed out successfully."
      });
      navigate('/auth', {
        replace: true
      });
    } catch (error) {
      toast({
        title: "Signed Out",
        description: "You have been signed out."
      });
      navigate('/auth', {
        replace: true
      });
    }
  };
  const getInitials = (name: string) => {
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase();
  };
  const getUserDisplayName = () => {
    if (!user) return 'User';
    if (profile?.display_name) return profile.display_name;
    if (profile?.first_name && profile?.last_name) return `${profile.first_name} ${profile.last_name}`;
    if (profile?.first_name) return profile.first_name;
    return user?.email?.split('@')[0] || 'User';
  };

  // Show loading state while auth is initializing
  if (authLoading) {
    return <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span>Loading...</span>
        </div>
      </div>;
  }

  // Handle different views for authenticated users
  if (user && user.email && !user.is_anonymous) {
    // Show Job Wizard
    if (currentView === 'job-wizard') {
      return <JobWizard onClose={handleBackToHome} onSave={handleJobSaved} />;
    }

    // Show Libraries Selector
    if (currentView === 'libraries') {
      return <div className="min-h-screen bg-background">
          <ResumeLibrary onUploadNew={handleOpenResumeAnalysis} onBack={handleBackToHome} initialTab={resumeLibraryInitialTab} />
        </div>;
    }

    // Show Job Library
    if (currentView === 'job-library') {
      return <JobLibrary savedJobs={savedJobs} onUploadNew={handleOpenJobWizard} onBack={handleBackToHome} onJobDeleted={handleJobDeleted} />;
    }

    // Show Resume Analysis
    if (currentView === 'resume-analysis') {
      return <ResumeAnalysis onBack={handleBackToHome} />;
    }

    // Show Resume Library
    if (currentView === 'resume-library') {
      return <div className="min-h-screen bg-background">
          <ResumeLibrary onUploadNew={handleOpenResumeAnalysis} onBack={handleBackToHome} initialTab={resumeLibraryInitialTab} autoSelectTailored={location.state?.autoSelectTailored} />
        </div>;
    }

    // Show Resume Match Tests - handled by ResumeLibrary now
    if (currentView === 'resume-match-tests') {
      return <div className="min-h-screen bg-background">
          <ResumeLibrary onUploadNew={handleOpenResumeAnalysis} onBack={handleBackToHome} initialTab="analyzed" />
        </div>;
    }

    // Show VectorShift Test
    if (currentView === 'vectorshift-test') {
      return <VectorShiftTest onBack={handleBackToHome} />;
    }

    // Show VectorShift Direct Test
    if (currentView === 'vectorshift-direct-test') {
      const VectorShiftDirectTest = lazy(() => import('./VectorShiftDirectTest'));
      return <Suspense fallback={<div>Loading...</div>}>
          <VectorShiftDirectTest />
        </Suspense>;
    }

    // Default home view
    return <AppLayout currentPage="home" variant="jobgpt">
        <ClientDashboardHome
          displayName={getUserDisplayName()}
          onUpgrade={() => navigate('/pricing')}
          onQualify={() => navigate('/qualification-wizard')}
          onResumeAnalysis={handleOpenResumeAnalysis}
          onLinkedIn={() => navigate('/linkedin-wizard-landing')}
          onMetrics={() => navigate('/metrics-wizard')}
        />
      </AppLayout>;
  }

  // Guest user view
  return <TailorWizMarketingHome />;
}